<template>
  <div class="appointment-products">
    <h2>预约商品管理</h2>
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <span>预约商品列表</span>
          <el-button type="primary" @click="showCreateDialog">
            <el-icon><Plus /></el-icon>
            新增商品
          </el-button>
        </div>
      </template>
      <div class="product-list-content">
        <!-- 搜索区域 -->
        <div class="search-area">
          <el-input
            v-model="searchForm.keyword"
            placeholder="请输入商品标题、简介"
            prefix-icon="Search"
            clearable
            class="search-input"
          />
          <div class="search-filters">
            <el-select v-model="searchForm.status" placeholder="商品状态" clearable>
              <el-option label="启用" value="active" />
              <el-option label="禁用" value="inactive" />
            </el-select>
            <el-select v-model="searchForm.appointment_status" placeholder="预约状态" clearable >
              <el-option label="即将开始" value="upcoming" />
              <el-option label="预约中" value="ongoing" />
              <el-option label="已结束" value="ended" />
              <el-option label="已约满" value="full" />
            </el-select>
            <el-button type="primary" @click="handleSearch">
              <el-icon><Search /></el-icon>
              搜索
            </el-button>
          </div>
        </div>

        <!-- 数据表格 -->
        <el-table
          v-loading="loading"
          :data="productList"
          stripe
          style="width: 100%"
          class="product-table"
          :row-key="row => row.id"
        >
          <el-table-column label="商品信息" >
            <template #default="scope">
              <div class="product-info-column">
                <div class="product-image-container">
                  <el-image
                    :src="getImageUrlWithDomain(scope.row.image)"
                    fit="cover"
                    style="width: 80px; height: 80px"
                  />
                </div>
                    
                <div class="product-details">
                     <div class="product-title" show-overflow-tooltip>
  <el-tag :type="scope.row.status === 'active' ? 'success' : 'danger'" style="margin-right: 8px;">
                  {{ scope.row.status === 'active' ? '已启用' : '已禁用' }}
                </el-tag>                  </div>
                  <div class="product-title" show-overflow-tooltip>
                    {{ scope.row.title }}
                  </div>
                  <div class="product-intro" show-overflow-tooltip>
                    {{ scope.row.intro }}
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="points_required" label="消耗积分" width="100" />
          <el-table-column label="预约时间" >
            <template #default="scope">
              <div>
                <!-- 状态标签 -->
                <div style="margin-bottom: 8px;">
                  <el-tag :type="getAppointmentStatusType(scope.row)">
                    {{ getAppointmentStatusText(scope.row) }}
                  </el-tag>
                </div>
                <!-- 时间信息 -->
                <div>
                  <div class="time-label">开始：</div>
                  <div>{{ formatDateTime(scope.row.appointment_start_time) }}</div>
                  <div class="time-label">结束：</div>
                  <div>{{ formatDateTime(scope.row.appointment_end_time) }}</div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="商品状态操作" width="120">
            <template #default="scope">
              <div class="status-column">
                <el-button 
                  size="small" 
                  :type="scope.row.status === 'active' ? 'danger' : 'success'"
                  @click="toggleProductStatus(scope.row)"
                >
                  {{ scope.row.status === 'active' ? '禁用' : '启用' }}
                </el-button>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作"  fixed="right">
            <template #default="scope">
              <el-button type="primary" size="small" @click="showEditDialog(scope.row)">
                <el-icon><Edit /></el-icon>
                编辑
              </el-button>
         
              <el-button
                type="info"
                size="small"
                @click="viewAppointmentRecords(scope.row)"
              >
                <el-icon><View /></el-icon>
                查看预约信息
              </el-button>
              
              <el-button
                type="danger"
                size="small"
                @click="handleDelete(scope.row)"
              >
                <el-icon><Delete /></el-icon>
                删除
              </el-button>
            </template>
          </el-table-column>

        </el-table>

        <!-- 分页 -->
        <div class="pagination-area">
          <el-pagination
            :current-page="pagination.currentPage"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 创建/编辑商品抽屉 -->
    <el-drawer
      v-model="dialogVisible"
      :title="dialogTitle"
      direction="rtl"
      size="1000px"
      :before-close="handleDialogClose"
      destroy-on-close
    >
      <div class="product-form-content">
        <!-- 自定义标签栏 - 放在el-tabs外面，直接作为product-form-content的子元素 -->
        <div class="custom-tabs">
          <div
            v-for="item in tabs"
            :key="item.name"
            :class="['custom-tab', { active: activeTab === item.name }]"
            @click="activeTab = item.name"
          >
            {{ item.label }}
          </div>
        </div>
        
        <!-- 标签页内容区域，设置为可滚动 -->
        <div class="tabs-content-wrapper">
          <el-tabs v-model="activeTab" type="card" class="no-tabs-header">
            <template #tabs>
              <!-- 隐藏默认标签 -->
            </template>
            <!-- 基础信息标签页 -->
            <el-tab-pane label="基础信息" name="basic">
              <el-form :model="formData" label-width="120px" class="product-form">
                <el-form-item label="商品标题" required>
                  <el-input v-model="formData.title" placeholder="请输入商品标题" maxlength="255" show-word-limit />
                </el-form-item>
              
                <el-form-item label="商品简介" required>
                <el-input
                  v-model="formData.intro"
                  type="textarea"
                  :rows="3"
                  placeholder="请输入商品简介"
                  maxlength="500"
                  show-word-limit
                />
              </el-form-item>
              
              <!-- 封面图 -->
              <el-form-item label="封面图" prop="image" required>
                <!-- 图片大小提示 -->
                <el-alert
                  title="图片上传提示"
                  type="info"
                  :closable="false"
                  show-icon
                  style="margin-bottom: 16px;"
                >
                  <template #default>
                    <div>基于当前服务器配置，建议您将上传的图片大小控制在200KB以下，以确保页面加载速度和最佳显示效果。</div>
                  </template>
                </el-alert>
                
                <!-- 只有当没有图片时才显示上传按钮 -->
                <div v-if="!formData.image" class="upload-button-container">
                  <el-upload
                    :http-request="customImageUpload"
                    list-type="picture-card"
                    :limit="1"
                    :on-error="handleImageUploadError"
                    :on-remove="handleImageRemove"
                    :file-list="imageFileList"
                    :auto-upload="true"
                  >
                    <el-icon><Plus /></el-icon>
                    <template #tip>
                      <div class="el-upload__tip">
                        仅支持 JPG、PNG 格式，不超过 2MB
                      </div>
                    </template>
                  </el-upload>
                </div>
                
                <!-- 显示已上传的图片 -->
                <div v-else class="uploaded-image-container">
                  <el-image
                    :src="getImageUrl(formData.image)"
                    style="width: 100px; height: 100px;"
                    fit="cover"
                  />
                  <el-button type="text" @click="removeImage" style="margin-top: 10px; color: #f56c6c;">
                    更换图片
                  </el-button>
                </div>
              </el-form-item>
              
              <!-- 商品滚图 -->
              <el-form-item label="商品滚图">
                <el-upload
                  :http-request="customImagesUpload"
                  list-type="picture-card"
                  :multiple="true"
                  :on-error="handleImageUploadError"
                  :on-remove="handleImageRemove"
                  :file-list="imagesFileList"
                  :auto-upload="true"
                  :on-preview="handlePictureCardPreview"
                >
                  <el-icon><Plus /></el-icon>
                  <template #tip>
                    <div class="el-upload__tip">
                      支持多张 JPG、PNG 格式，每张不超过 2MB
                    </div>
                  </template>
                </el-upload>
                <el-dialog v-model="previewVisible" title="预览图片">
                  <img style="width: 100%" :src="dialogImageUrl" alt="预览图片" />
                </el-dialog>
                <div class="form-tip">
                  已上传 {{ imagesFileList.length }} 张图片
                </div>
              </el-form-item>
              
              <!-- 详情编辑 -->
              <el-form-item label="商品详情" required>
                <div class="rich-text-editor">
                  <RichTextEditor v-model="formData.description" :height="300" placeholder="请输入商品详情" />
                </div>
              </el-form-item>
              
              <!-- 预约消耗积分数量 -->
              <el-form-item label="消耗积分" required>
                <el-input-number v-model="formData.points_required" :min="0" placeholder="请输入预约消耗积分数量" />
              </el-form-item>
            </el-form>
          </el-tab-pane>
          
          <!-- 预约条件标签页 -->
          <el-tab-pane label="预约条件设置" name="appointment">
            <el-form :model="formData" label-width="120px" class="product-form">
              <!-- 可预约会员身份选择 -->
              <el-form-item label="可预约会员身份">
                <el-select
                  v-model="formData.available_member_levels"
                  placeholder="请选择可预约会员身份"
                  multiple
                  :loading="loadingMemberLevels"
                >
                  <el-option
                    v-for="level in memberLevels"
                    :key="level.id"
                    :label="level.level_name"
                    :value="String(level.id)"
                  />
                </el-select>
               
              </el-form-item>
              
              <!-- 预约开始时间 -->
              <el-form-item label="预约开始时间" required>
                <el-date-picker
                  v-model="formData.appointment_start_time"
                  type="datetime"
                  placeholder="选择开始时间"
                  format="YYYY-MM-DD HH:mm:ss"
                  value-format="YYYY-MM-DD HH:mm:ss"
                />
              </el-form-item>
              
              <!-- 预约结束时间 -->
              <el-form-item label="预约结束时间" required>
                <el-date-picker
                  v-model="formData.appointment_end_time"
                  type="datetime"
                  placeholder="选择结束时间"
                  format="YYYY-MM-DD HH:mm:ss"
                  value-format="YYYY-MM-DD HH:mm:ss"
                  :disabled-date="disabledDate"
                />
              </el-form-item>
              
              <!-- 上限人数设置 -->
              <el-form-item label="上限人数" required>
                <el-input-number v-model="formData.max_participants" :min="0" placeholder="请输入上限人数设置" />
              </el-form-item>
              
              <!-- 商品状态 -->
              <el-form-item label="商品状态" required>
                <el-select v-model="formData.status" placeholder="请选择商品状态">
                  <el-option label="启用" value="active" />
                  <el-option label="禁用" value="inactive" />
                </el-select>
              </el-form-item>
            </el-form>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">
            {{ isEditMode ? '保存修改' : '创建商品' }}
          </el-button>
        </div>
      </template>
    </el-drawer>
  </div>
</template>

<style scoped>
/* 表格样式优化 */
.product-table {
  font-size: 14px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

/* 商品信息列样式 */
.product-info-column {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}

.product-image-container {
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;
}

.product-image-container:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.product-details {
  flex: 1;
  min-width: 0;
}

.product-title {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
  font-size: 14px;
}

.product-intro {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* 时间标签样式 */
.time-label {
  font-size: 12px;
  color: #999;
  margin-right: 8px;
}

/* 搜索区域样式 */
.search-area {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  align-items: center;
  flex-wrap: wrap;
}

.search-input {
  width: 300px;
}

.search-filters {
  display: flex;
  gap: 12px;
  align-items: center;
}

/* 卡片和标题样式 */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.product-list-content {
  padding: 0;
}

/* 分页区域样式 */
.pagination-area {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding: 16px 0 0;
  border-top: 1px solid #f0f0f0;
}
</style>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Plus, Edit, View, Delete } from '@element-plus/icons-vue';
import RichTextEditor from '../../components/RichTextEditor.vue';
import * as appointmentProductApi from '../../api/appointmentProducts';
import { fetchMemberLevelOptions } from '../../api/member';
import { useRouter } from 'vue-router';

export default {
  name: 'AppointmentProducts',
  components: {
    Search,
    Plus,
    Edit,
    View,
    Delete,
    RichTextEditor
  },
  setup() {
    // 路由实例
    const router = useRouter();
    // 搜索表单
    const searchForm = reactive({
      keyword: '',
      status: '',
      appointment_status: ''
    });

    // 加载状态
    const loading = ref(false);

    // 商品列表数据
    const productList = ref([]);

    // 分页数据
    const pagination = reactive({
      currentPage: 1,
      pageSize: 10,
      total: 0
    });
    
    // 弹窗相关
    const dialogVisible = ref(false);
    const dialogTitle = ref('新增商品');
    const isEditMode = ref(false);
    const activeTab = ref('basic');
    
    // 标签页数据已移除，直接在模板中使用
    
    // 表单数据
    const formData = reactive({
      title: '',
      intro: '',
      image: '',
      images: [],
      description: '',
      points_required: 0,
      available_member_levels: [],
      appointment_start_time: '',
      appointment_end_time: '',
      max_participants: 0,
      status: 'active'
    });
    
    // API基础地址
    const apiBaseUrl = ref(process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api');
    
    // 文件上传相关
    const imageFileList = ref([]);
    const imagesFileList = ref([]);
    const previewVisible = ref(false);
    const dialogImageUrl = ref('');

    // 会员等级列表
    const memberLevels = ref([]);
    // 是否正在加载会员等级
    const loadingMemberLevels = ref(false);

    // 加载商品列表
    const loadProducts = async () => {
      loading.value = true;
      try {
        const params = {
          page: pagination.currentPage,
          per_page: pagination.pageSize,
          keyword: searchForm.keyword,
          status: searchForm.status,
          appointment_status: searchForm.appointment_status
        };
        const response = await appointmentProductApi.fetchAppointmentProductsList(params);
        if (response.code === 200) {
          // 检查响应数据结构，适配不同的返回格式
          if (response.data.items) {
            productList.value = response.data.items;
            pagination.total = response.data.pagination?.total || 0;
          } else if (response.data.data) {
            // 适配直接返回分页数据的格式
            productList.value = response.data.data;
            pagination.total = response.data.total || 0;
          } else {
            // 适配直接返回数组的格式
            productList.value = Array.isArray(response.data) ? response.data : [];
            pagination.total = productList.value.length;
          }
        } else {
          ElMessage.error(`获取商品列表失败: ${response.message || '未知错误'}`);
        }
      } catch (error) {
        ElMessage.error('获取商品列表失败');
        console.error('请求失败:', error);
      } finally {
        loading.value = false;
      }
    };

    // 搜索商品
    const handleSearch = () => {
      pagination.currentPage = 1;
      loadProducts();
    };

    // 获取会员等级列表
    const fetchMemberLevels = async () => {
        loadingMemberLevels.value = true;
        try {
            const response = await fetchMemberLevelOptions();
            console.log('fetchMemberLevels原始响应:', response);
            console.log('fetchMemberLevels响应code:', response.code);
            console.log('fetchMemberLevels响应data:', response.data);
            if (response.code === 200) {
                memberLevels.value = response.data;
                console.log('会员等级列表加载成功:', memberLevels.value);
            } else {
                console.error('会员等级列表加载失败，code:', response.code, 'message:', response.message);
            }
        } catch (error) {
            console.error('获取会员等级失败:', error);
            ElMessage.error('获取会员等级失败');
        } finally {
            loadingMemberLevels.value = false;
        }
    };

    // 显示创建弹窗
    const showCreateDialog = async () => {
      resetForm();
      dialogTitle.value = '新增商品';
      isEditMode.value = false;

      // 确保会员等级列表已加载
      if (memberLevels.value.length === 0) {
        await fetchMemberLevels();
      }

      dialogVisible.value = true;
      activeTab.value = 'basic';
    };
    
    // 显示编辑弹窗
    const showEditDialog = async (row) => {
      resetForm();
      dialogTitle.value = '编辑商品';
      isEditMode.value = true;

      // 确保会员等级列表已加载
      if (memberLevels.value.length === 0) {
        await fetchMemberLevels();
      }

      try {
        const response = await appointmentProductApi.fetchAppointmentProductDetail(row.id);
        console.log('完整的API响应:', response);
        console.log('API响应数据结构:', Object.keys(response.data));
        console.log('API响应原始数据:', JSON.stringify(response.data));
        
        if (response.code === 200) {
          console.log('API响应完整数据:', JSON.stringify(response));
          const productData = response.data;
          console.log('productData结构:', Object.keys(productData));
          console.log('productData原始值:', JSON.stringify(productData));
          
          let product = productData.product;
          console.log('product是否存在:', !!product);
          if (product) {
            console.log('product结构:', Object.keys(product));
            console.log('product.image:', product.image);
            console.log('product.images:', product.images);
            console.log('product.images类型:', typeof product.images);
            console.log('product.images是否为数组:', Array.isArray(product.images));
            console.log('product.images原始值:', JSON.stringify(product.images));
            console.log('product.images长度:', Array.isArray(product.images) ? product.images.length : 'N/A');
          } else {
            console.log('警告: product不存在，使用response.data作为product');
            product = response.data;
            console.log('使用response.data作为product:', !!product);
            if (product) {
              console.log('response.data结构:', Object.keys(product));
            }
          }
          
          // 填充表单数据
          formData.title = product?.title || '';
          formData.intro = product?.intro || '';
          formData.description = product?.description || '';
          formData.points_required = product?.points_required || 0;

          // 处理会员等级，确保是数组格式
          if (product?.available_member_levels) {
            if (Array.isArray(product.available_member_levels)) {
              formData.available_member_levels = product.available_member_levels.map(level => String(level));
            } else if (typeof product.available_member_levels === 'string') {
              try {
                formData.available_member_levels = JSON.parse(product.available_member_levels).map(level => String(level));
              } catch (e) {
                formData.available_member_levels = [];
              }
            } else {
              formData.available_member_levels = [];
            }
          } else {
            formData.available_member_levels = [];
          }

          formData.appointment_start_time = product?.appointment_start_time || '';
          formData.appointment_end_time = product?.appointment_end_time || '';
          formData.max_participants = product?.max_participants || 0;
          formData.status = product?.status || 'active';
          if (product?.id) {
            formData.id = product.id;
          }
          
          // 处理封面图
          if (product?.image) {
            let previewUrl = product.image;
            // 预览需要完整URL，使用API基础域名
            const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
            const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
            const apiDomain = domainMatch ? domainMatch[1] : window.location.origin;
            console.log('使用的API基础域名:', apiDomain);
            
            if (previewUrl && (previewUrl.startsWith('http://') || previewUrl.startsWith('https://'))) {
              // 如果是完整路径，替换为API基础域名
              const url = new URL(previewUrl);
              const relativePath = url.pathname + url.search + url.hash;
              previewUrl = `${apiDomain}${relativePath}`;
              console.log('封面图替换域名:', previewUrl);
            } else if (previewUrl) {
              // 如果是相对路径，添加API基础域名
              previewUrl = `${apiDomain}${previewUrl}`;
              console.log('封面图添加域名:', previewUrl);
            }
            imageFileList.value = [{ 
              name: '封面图', 
              url: previewUrl, 
              uid: Date.now(),
              status: 'success'
            }];
            formData.image = product.image;
            console.log('封面图处理成功:', product.image);
          }
          
          // 处理商品滚图
          const tempImagesList = [];
          const tempFormImages = [];
          
          // 尝试多种方式处理images数据
          if (product?.images) {
            // 方式1: 直接作为数组处理
            if (Array.isArray(product.images)) {
              console.log('方式1: images是数组');
              console.log('数组长度:', product.images.length);
              product.images.forEach((img, index) => {
                console.log(`数组元素${index}:`, img);
                console.log(`数组元素${index}类型:`, typeof img);
                let imageUrl = '';
                if (typeof img === 'string') {
                  imageUrl = img;
                  console.log(`数组元素${index}是字符串:`, imageUrl);
                } else if (typeof img === 'object' && img !== null) {
                  if (img.url) {
                    imageUrl = img.url;
                    console.log(`数组元素${index}是对象，有url属性:`, imageUrl);
                  } else if (img.path) {
                    imageUrl = img.path;
                    console.log(`数组元素${index}是对象，有path属性:`, imageUrl);
                  } else {
                    console.log(`数组元素${index}是对象，无url或path属性:`, img);
                  }
                } else {
                  console.log(`数组元素${index}类型未知:`, img);
                }
                
                if (imageUrl) {
                  // 确保图片URL使用正确的域名
                  let previewUrl = imageUrl;
                  // 获取API基础URL和域名
                  const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
                  const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
                  const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
                  console.log('使用的基础域名:', baseDomain);
                  
                  // 无论是否是完整路径，都使用API基础域名
                  if (previewUrl && (previewUrl.startsWith('http://') || previewUrl.startsWith('https://'))) {
                    // 如果是完整路径，替换为API基础域名
                    const url = new URL(previewUrl);
                    const relativePath = url.pathname + url.search + url.hash;
                    previewUrl = `${baseDomain}${relativePath}`;
                    console.log(`数组元素${index}替换域名:`, previewUrl);
                  } else if (previewUrl) {
                    // 如果是相对路径，添加API基础域名
                    previewUrl = `${baseDomain}${previewUrl}`;
                    console.log(`数组元素${index}添加域名:`, previewUrl);
                  }
                  
                  tempImagesList.push({
                    name: `图片${index + 1}`,
                    url: previewUrl,
                    uid: Date.now() + index,
                    status: 'success'
                  });
                  
                  // 对于formData.images，存储相对路径
                  let formImageUrl = imageUrl;
                  if (formImageUrl && (formImageUrl.startsWith('http://') || formImageUrl.startsWith('https://'))) {
                    // 如果是完整路径，提取相对路径
                    const url = new URL(formImageUrl);
                    formImageUrl = url.pathname + url.search + url.hash;
                    console.log(`数组元素${index}提取相对路径:`, formImageUrl);
                  }
                  tempFormImages.push(formImageUrl);
                  console.log(`数组元素${index}已添加到临时列表`);
                }
              });
            }
            // 方式2: 作为字符串处理，尝试解析为JSON
            else if (typeof product.images === 'string') {
              console.log('方式2: images是字符串');
              try {
                // 尝试解析JSON字符串
                const parsedImages = JSON.parse(product.images);
                if (Array.isArray(parsedImages)) {
                  parsedImages.forEach((img, index) => {
                    let imageUrl = '';
                    if (typeof img === 'string') {
                      imageUrl = img;
                    } else if (typeof img === 'object' && img !== null && img.url) {
                      imageUrl = img.url;
                    }
                    
                    if (imageUrl) {
                      // 确保图片URL使用正确的域名
                      let previewUrl = imageUrl;
                      // 获取API基础URL和域名
                      const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
                      const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
                      const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
                      console.log('使用的基础域名:', baseDomain);
                      
                      // 无论是否是完整路径，都使用API基础域名
                      if (previewUrl && (previewUrl.startsWith('http://') || previewUrl.startsWith('https://'))) {
                        // 如果是完整路径，替换为API基础域名
                        const url = new URL(previewUrl);
                        const relativePath = url.pathname + url.search + url.hash;
                        previewUrl = `${baseDomain}${relativePath}`;
                        console.log(`解析数组元素${index}替换域名:`, previewUrl);
                      } else if (previewUrl) {
                        // 如果是相对路径，添加API基础域名
                        previewUrl = `${baseDomain}${previewUrl}`;
                        console.log(`解析数组元素${index}添加域名:`, previewUrl);
                      }
                      
                      tempImagesList.push({
                        name: `图片${index + 1}`,
                        url: previewUrl,
                        uid: Date.now() + index,
                        status: 'success'
                      });
                      
                      // 对于formData.images，存储相对路径
                      let formImageUrl = imageUrl;
                      if (formImageUrl && (formImageUrl.startsWith('http://') || formImageUrl.startsWith('https://'))) {
                        // 如果是完整路径，提取相对路径
                        const url = new URL(formImageUrl);
                        formImageUrl = url.pathname + url.search + url.hash;
                        console.log(`解析数组元素${index}提取相对路径:`, formImageUrl);
                      }
                      tempFormImages.push(formImageUrl);
                    }
                  });
                }
                // 如果解析后不是数组，尝试作为单个图片URL处理
                else if (typeof parsedImages === 'string') {
                  let imageUrl = parsedImages;
                  // 确保图片URL使用正确的域名
                  let previewUrl = imageUrl;
                  // 获取API基础URL和域名
                  const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
                  const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
                  const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
                  console.log('使用的基础域名:', baseDomain);
                  
                  // 无论是否是完整路径，都使用API基础域名
                  if (previewUrl && (previewUrl.startsWith('http://') || previewUrl.startsWith('https://'))) {
                    // 如果是完整路径，替换为API基础域名
                    const url = new URL(previewUrl);
                    const relativePath = url.pathname + url.search + url.hash;
                    previewUrl = `${baseDomain}${relativePath}`;
                    console.log('解析单个字符串替换域名:', previewUrl);
                  } else if (previewUrl) {
                    // 如果是相对路径，添加API基础域名
                    previewUrl = `${baseDomain}${previewUrl}`;
                    console.log('解析单个字符串添加域名:', previewUrl);
                  }
                  
                  tempImagesList.push({
                    name: '图片1',
                    url: previewUrl,
                    uid: Date.now(),
                    status: 'success'
                  });
                  
                  // 对于formData.images，存储相对路径
                  let formImageUrl = imageUrl;
                  if (formImageUrl && (formImageUrl.startsWith('http://') || formImageUrl.startsWith('https://'))) {
                    // 如果是完整路径，提取相对路径
                    const url = new URL(formImageUrl);
                    formImageUrl = url.pathname + url.search + url.hash;
                    console.log('解析单个字符串提取相对路径:', formImageUrl);
                  }
                  tempFormImages.push(formImageUrl);
                }
              } catch (e) {
                console.log('解析JSON失败，尝试作为单个图片URL处理:', e);
                // 解析失败，尝试作为单个图片URL处理
                let imageUrl = product.images;
                // 确保图片URL使用正确的域名
                let previewUrl = imageUrl;
                // 获取API基础URL和域名
                const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
                const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
                const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
                console.log('使用的基础域名:', baseDomain);
                
                // 无论是否是完整路径，都使用API基础域名
                if (previewUrl && (previewUrl.startsWith('http://') || previewUrl.startsWith('https://'))) {
                  // 如果是完整路径，替换为API基础域名
                  const url = new URL(previewUrl);
                  const relativePath = url.pathname + url.search + url.hash;
                  previewUrl = `${baseDomain}${relativePath}`;
                  console.log('解析失败替换域名:', previewUrl);
                } else if (previewUrl) {
                  // 如果是相对路径，添加API基础域名
                  previewUrl = `${baseDomain}${previewUrl}`;
                  console.log('解析失败添加域名:', previewUrl);
                }
                
                tempImagesList.push({
                  name: '图片1',
                  url: previewUrl,
                  uid: Date.now(),
                  status: 'success'
                });
                
                // 对于formData.images，存储相对路径
                let formImageUrl = imageUrl;
                if (formImageUrl && (formImageUrl.startsWith('http://') || formImageUrl.startsWith('https://'))) {
                  // 如果是完整路径，提取相对路径
                  const url = new URL(formImageUrl);
                  formImageUrl = url.pathname + url.search + url.hash;
                  console.log('解析失败提取相对路径:', formImageUrl);
                }
                tempFormImages.push(formImageUrl);
              }
            }
            // 方式3: 作为对象处理
            else if (typeof product.images === 'object' && product.images !== null) {
              console.log('方式3: images是对象');
              // 检查是否有url属性
              if (product.images.url) {
                let imageUrl = product.images.url;
                // 确保图片URL使用正确的域名
                let previewUrl = imageUrl;
                // 获取API基础URL和域名
                const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
                const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
                const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
                console.log('使用的基础域名:', baseDomain);
                
                // 无论是否是完整路径，都使用API基础域名
                if (previewUrl && (previewUrl.startsWith('http://') || previewUrl.startsWith('https://'))) {
                  // 如果是完整路径，替换为API基础域名
                  const url = new URL(previewUrl);
                  const relativePath = url.pathname + url.search + url.hash;
                  previewUrl = `${baseDomain}${relativePath}`;
                  console.log('对象url替换域名:', previewUrl);
                } else if (previewUrl) {
                  // 如果是相对路径，添加API基础域名
                  previewUrl = `${baseDomain}${previewUrl}`;
                  console.log('对象url添加域名:', previewUrl);
                }
                
                tempImagesList.push({
                  name: '图片1',
                  url: previewUrl,
                  uid: Date.now(),
                  status: 'success'
                });
                
                // 对于formData.images，存储相对路径
                let formImageUrl = imageUrl;
                if (formImageUrl && (formImageUrl.startsWith('http://') || formImageUrl.startsWith('https://'))) {
                  // 如果是完整路径，提取相对路径
                  const url = new URL(formImageUrl);
                  formImageUrl = url.pathname + url.search + url.hash;
                  console.log('对象url提取相对路径:', formImageUrl);
                }
                tempFormImages.push(formImageUrl);
              }
              // 检查是否有其他可能的图片属性
              else if (product.images.path) {
                let imageUrl = product.images.path;
                // 确保图片URL使用正确的域名
                let previewUrl = imageUrl;
                // 获取API基础URL和域名
                const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
                const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
                const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
                console.log('使用的基础域名:', baseDomain);
                
                // 无论是否是完整路径，都使用API基础域名
                if (previewUrl && (previewUrl.startsWith('http://') || previewUrl.startsWith('https://'))) {
                  // 如果是完整路径，替换为API基础域名
                  const url = new URL(previewUrl);
                  const relativePath = url.pathname + url.search + url.hash;
                  previewUrl = `${baseDomain}${relativePath}`;
                  console.log('对象path替换域名:', previewUrl);
                } else if (previewUrl) {
                  // 如果是相对路径，添加API基础域名
                  previewUrl = `${baseDomain}${previewUrl}`;
                  console.log('对象path添加域名:', previewUrl);
                }
                
                tempImagesList.push({
                  name: '图片1',
                  url: previewUrl,
                  uid: Date.now(),
                  status: 'success'
                });
                
                // 对于formData.images，存储相对路径
                let formImageUrl = imageUrl;
                if (formImageUrl && (formImageUrl.startsWith('http://') || formImageUrl.startsWith('https://'))) {
                  // 如果是完整路径，提取相对路径
                  const url = new URL(formImageUrl);
                  formImageUrl = url.pathname + url.search + url.hash;
                  console.log('对象path提取相对路径:', formImageUrl);
                }
                tempFormImages.push(formImageUrl);
              }
            }
          } else {
            console.log('product.images为null或undefined');
          }
          
          console.log('临时图片列表:', tempImagesList);
          console.log('临时图片列表长度:', tempImagesList.length);
          console.log('临时formImages列表:', tempFormImages);
          console.log('临时formImages列表长度:', tempFormImages.length);
          
          // 等待Vue更新
          setTimeout(() => {
            imagesFileList.value = [...tempImagesList];
            formData.images = [...tempFormImages];
            
            console.log('最终imagesFileList:', imagesFileList.value);
            console.log('最终imagesFileList长度:', imagesFileList.value.length);
            console.log('最终formData.images:', formData.images);
            console.log('最终formData.images长度:', formData.images.length);
            
            // 验证imagesFileList中的每个元素格式
            imagesFileList.value.forEach((img, index) => {
              console.log(`图片${index}格式:`, {
                name: img.name,
                url: img.url,
                uid: img.uid,
                status: img.status,
                hasAllFields: !!img.name && !!img.url && !!img.uid && !!img.status
              });
            });
            
            // 确保所有数据都填充完成后再显示对话框，给Vue足够的时间处理响应式更新
            setTimeout(() => {
              console.log('即将显示对话框，此时的imagesFileList:', imagesFileList.value);
              console.log('即将显示对话框，此时的imagesFileList长度:', imagesFileList.value.length);
              dialogVisible.value = true;
              activeTab.value = 'basic';
              
              // 对话框显示后再次检查，确保数据正确
              setTimeout(() => {
                console.log('对话框显示后的imagesFileList:', imagesFileList.value);
                console.log('对话框显示后的imagesFileList长度:', imagesFileList.value.length);
              }, 300);
            }, 100);
          }, 50);
        } else {
          ElMessage.error(`获取商品详情失败: ${response.message || '未知错误'}`);
        }
      } catch (error) {
        ElMessage.error('获取商品详情失败');
        console.error('请求失败:', error);
      }
    };
    
    // 重置表单
    const resetForm = () => {
      Object.assign(formData, {
        title: '',
        intro: '',
        image: '',
        images: [],
        description: '',
        points_required: 0,
        available_member_levels: [],
        appointment_start_time: '',
        appointment_end_time: '',
        max_participants: 0,
        status: 'active'
      });
      imageFileList.value = [];
      imagesFileList.value = [];
    };
    
    // 处理表单提交
    const handleSubmit = async () => {
      try {
        let response;
        if (isEditMode.value) {
          response = await appointmentProductApi.updateAppointmentProduct(formData.id, formData);
        } else {
          response = await appointmentProductApi.createAppointmentProduct(formData);
        }
        
        if (response.code === 200) {
          ElMessage.success(isEditMode.value ? '商品更新成功' : '商品创建成功');
          dialogVisible.value = false;
          loadProducts();
        } else {
          ElMessage.error(isEditMode.value ? '商品更新失败' : '商品创建失败');
        }
      } catch (error) {
        ElMessage.error(isEditMode.value ? '商品更新失败' : '商品创建失败');
        console.error(error);
      }
    };
    
    // 处理弹窗关闭
    const handleDialogClose = () => {
      resetForm();
      dialogVisible.value = false;
    };
    
    // 处理图片上传成功
    const handleImageUploadSuccess = (response, file, fileList) => {
      // 处理后端返回的errno格式
      if (response.errno === 0) {
        // 获取相对路径，不添加完整域名
        let imgUrl = response.data[0].url;
        // 上传成功，设置图片URL（只保存相对路径）
        formData.image = imgUrl;
        // 更新文件列表，文件列表中的预览URL需要完整地址，但保存到数据库的是相对路径
        const updatedFileList = fileList.map(item => {
          if (item.response && item.response.errno === 0 && item.response.data && item.response.data.length > 0) {
            let fileUrl = item.response.data[0].url;
            let previewUrl = fileUrl;
            // 预览需要完整URL，使用API域名
            if (previewUrl && !previewUrl.startsWith('http://') && !previewUrl.startsWith('https://')) {
              // 从API基础URL中提取域名
              const apiDomain = window.location.origin;
              previewUrl = `${apiDomain}${previewUrl}`;
            }
            return {
              ...item,
              url: previewUrl // 预览使用完整URL
            };
          }
          return item;
        });
        // 保存更新后的文件列表到响应式变量
        imageFileList.value = [...updatedFileList];
        ElMessage.success('封面图上传成功');
      } else {
        ElMessage.error('图片上传失败: ' + (response.message || '未知错误'));
      }
    };
    
    // 处理多图上传成功
    const handleImagesUploadSuccess = (response, file, fileList) => {
      // 处理后端返回的errno格式
      if (response.errno === 0) {
        // 获取相对路径，不添加完整域名
        let imgUrl = response.data[0].url;
        // 上传成功，设置图片URL（只保存相对路径）
        formData.images.push(imgUrl);
        // 更新文件列表，文件列表中的预览URL需要完整地址，但保存到数据库的是相对路径
        const updatedFileList = fileList.map(item => {
          if (item.response && item.response.errno === 0 && item.response.data && item.response.data.length > 0) {
            let fileUrl = item.response.data[0].url;
            let previewUrl = fileUrl;
            // 预览需要完整URL，使用API域名
            if (previewUrl && !previewUrl.startsWith('http://') && !previewUrl.startsWith('https://')) {
              // 从API基础URL中提取域名
              const apiDomain = window.location.origin;
              previewUrl = `${apiDomain}${previewUrl}`;
            }
            return {
              ...item,
              url: previewUrl // 预览使用完整URL
            };
          }
          return item;
        });
        // 保存更新后的文件列表到响应式变量
        imagesFileList.value = [...updatedFileList];
        ElMessage.success('图片上传成功');
      } else {
        ElMessage.error('图片上传失败: ' + (response.message || '未知错误'));
      }
    };
    
    // 处理图片上传错误
    const handleImageUploadError = () => {
      ElMessage.error('图片上传失败');
    };
    
    // 处理图片移除
    const handleImageRemove = (file, fileList) => {
      // 处理封面图移除
      if (file.url === formData.image) {
        formData.image = '';
        imageFileList.value = [];
      }
      // 处理商品滚图移除
      else if (file.url) {
        // 从 formData.images 中移除
        const index = formData.images.findIndex(img => {
          // 处理完整URL和相对URL的情况
          if (img === file.url) {
            return true;
          }
          // 如果 file.url 是完整URL，检查 img 是否是其中的相对路径部分
          if (file.url.includes(img)) {
            return true;
          }
          // 处理相对URL和完整URL的情况
          if (img.includes(file.url)) {
            return true;
          }
          return false;
        });
        if (index > -1) {
          formData.images.splice(index, 1);
        }
        
        // 更新 imagesFileList
        imagesFileList.value = [...fileList];
      }
    };
    
    // 处理图片预览
    const handlePictureCardPreview = (file) => {
      dialogImageUrl.value = file.url;
      previewVisible.value = true;
    };
    
    // 清除已上传的图片
    const removeImage = () => {
      formData.image = '';
      imageFileList.value = [];
      ElMessage.success('已清除图片，您可以上传新的图片');
    };
    
    // 获取完整的图片URL
    const getImageUrl = (imagePath) => {
      if (!imagePath) return '';
      
      // 检查是否已经是完整的URL
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
      }
      
      // 获取API基础URL和域名
      const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
      const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
      const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
      
      // 确保路径以/开头
      const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
      
      return `${baseDomain}${normalizedPath}`;
    };
    
    // 获取带域名的图片URL（用于商品列表）
    const getImageUrlWithDomain = (imagePath) => {
      if (!imagePath) return '';
      
      // 获取API基础URL和域名
      const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
      const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
      const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
      
      let imageUrl = imagePath;
      
      // 无论是否是完整路径，都使用API基础域名
      if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
        // 如果是完整路径，替换为API基础域名
        const url = new URL(imageUrl);
        const relativePath = url.pathname + url.search + url.hash;
        imageUrl = `${baseDomain}${relativePath}`;
      } else if (imageUrl) {
        // 如果是相对路径，添加API基础域名
        imageUrl = `${baseDomain}${imageUrl}`;
      }
      
      return imageUrl;
    };
    
    // 自定义图片上传函数
    const customImageUpload = async (options) => {
      try {
        const { file } = options;
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('type', 'product');
        
        // 获取API基础URL和认证token
        const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
        const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
        const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
        const authToken = localStorage.getItem('token');
        
        // 直接使用axios而不是全局request，避免响应拦截器影响
        const axios = await import('axios');
        const response = await axios.default({
          url: `${apiBaseUrl}/manage/upload/image`,
          method: 'post',
          data: uploadFormData,
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
          },
          timeout: 15000
        });
        
        const res = response.data;
        // 处理wangEditor格式响应
        if (res.errno === 0 && res.data && res.data.length > 0) {
          let imageUrl = res.data[0].url;
          // 确保返回的是完整的绝对路径
          if (imageUrl && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
            imageUrl = `${baseDomain}${imageUrl}`;
          }
          formData.image = res.data[0].url;
          imageFileList.value = [{ 
            name: '封面图', 
            url: imageUrl, 
            uid: Date.now(), 
            status: 'success' 
          }];
          ElMessage.success('封面图上传成功');
          options.onSuccess();
        } else {
          ElMessage.error(res.message || '封面图上传失败');
          options.onError();
        }
      } catch (error) {
        console.error('图片上传失败:', error);
        ElMessage.error('封面图上传失败');
        options.onError();
      }
    };
    
    // 自定义商品滚图上传函数
    const customImagesUpload = async (options) => {
      try {
        const { file } = options;
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('type', 'product');
        
        // 获取API基础URL和认证token
        const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
        const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
        const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
        const authToken = localStorage.getItem('token');
        
        // 直接使用axios而不是全局request，避免响应拦截器影响
        const axios = await import('axios');
        const response = await axios.default({
          url: `${apiBaseUrl}/manage/upload/image`,
          method: 'post',
          data: uploadFormData,
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
          },
          timeout: 15000
        });
        
        const res = response.data;
        // 处理wangEditor格式响应
        if (res.errno === 0 && res.data && res.data.length > 0) {
          let imageUrl = res.data[0].url;
          // 确保返回的是完整的绝对路径
          if (imageUrl && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
            imageUrl = `${baseDomain}${imageUrl}`;
          }
          
          // 添加到 formData.images
          formData.images.push(res.data[0].url);
          
          // 添加到 imagesFileList
          const newImage = {
            name: file.name,
            url: imageUrl,
            uid: Date.now(),
            status: 'success'
          };
          imagesFileList.value.push(newImage);
          
          ElMessage.success('图片上传成功');
          options.onSuccess(newImage);
        } else {
          ElMessage.error(res.message || '图片上传失败');
          options.onError();
        }
      } catch (error) {
        console.error('图片上传失败:', error);
        ElMessage.error('图片上传失败');
        options.onError();
      }
    };
    
    // 删除商品
    const handleDelete = (row) => {
      ElMessageBox.confirm('删除后将无法查看相关预约记录，是否继续？', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(async () => {
          try {
            const response = await appointmentProductApi.deleteAppointmentProduct(row.id);
            if (response.code === 200) {
              ElMessage.success('商品删除成功');
              loadProducts();
            } else {
              ElMessage.error('商品删除失败');
            }
          } catch (error) {
            ElMessage.error('商品删除失败');
            console.error(error);
          }
        })
        .catch(() => {
          // 用户取消删除
        });
    };
    
    // 切换商品状态（启用/禁用）
    const toggleProductStatus = (row) => {
      // 确认操作
      const newStatus = row.status === 'active' ? '禁用' : '启用';
      ElMessageBox.confirm(`确定要${newStatus}该商品吗？`, '状态变更确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(async () => {
          try {
            // 调用API更新状态
            const response = await appointmentProductApi.updateAppointmentProductStatus(
              row.id, 
              row.status === 'active' ? 'inactive' : 'active'
            );
            if (response.code === 200) {
              // 更新成功，刷新列表
              ElMessage.success(`商品${newStatus}成功`);
              loadProducts();
            } else {
              ElMessage.error(`商品${newStatus}失败`);
            }
          } catch (error) {
            ElMessage.error(`商品${newStatus}失败`);
            console.error(error);
          }
        })
        .catch(() => {
          // 用户取消操作
        });
    };
    
    // 日期禁用规则
    const disabledDate = (time) => {
      if (!formData.appointment_start_time) return false;
      return time.getTime() < new Date(formData.appointment_start_time).getTime();
    };

    // 分页大小变化
    const handleSizeChange = (size) => {
      pagination.pageSize = size;
      loadProducts();
    };

    // 当前页变化
    const handleCurrentChange = (current) => {
      pagination.currentPage = current;
      loadProducts();
    };

    // 格式化日期时间为 yyyy-MM-dd HH:mm:ss 格式
    const formatDateTime = (dateTime) => {
      if (!dateTime) return '';
      const date = new Date(dateTime);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };
    
    // 获取预约状态文本
    const getAppointmentStatusText = (row) => {
      const now = new Date();
      const startTime = new Date(row.appointment_start_time);
      const endTime = new Date(row.appointment_end_time);
      
      if (now < startTime) {
        return '即将开始';
      } else if (now >= startTime && now <= endTime) {
        return '预约中';
      } else {
        return '已结束';
      }
    };
    
    // 获取预约状态标签类型
    const getAppointmentStatusType = (row) => {
      const now = new Date();
      const startTime = new Date(row.appointment_start_time);
      const endTime = new Date(row.appointment_end_time);
      
      if (now < startTime) {
        return 'warning'; // 即将开始 - 黄色
      } else if (now >= startTime && now <= endTime) {
        return 'success'; // 预约中 - 绿色
      } else {
        return 'info'; // 已结束 - 蓝色
      }
    };

    // 查看预约信息
    const viewAppointmentRecords = (row) => {
      router.push({
        path: '/appointment-records',
        query: {
          product_id: row.id,
          product_name: row.title
        }
      });
    };

    // 组件挂载时加载数据
    onMounted(() => {
      loadProducts();
    });

    return {
      searchForm,
      loading,
      productList,
      pagination,
      dialogVisible,
      dialogTitle,
      isEditMode,
      activeTab,
      formData,
      imageFileList,
      imagesFileList,
      previewVisible,
      dialogImageUrl,
      apiBaseUrl,
      formatDateTime,
      getAppointmentStatusText,
      getAppointmentStatusType,
      viewAppointmentRecords,
      handleSearch,
      showCreateDialog,
      showEditDialog,
      handleSubmit,
      handleDialogClose,
      handleImageUploadSuccess,
      handleImagesUploadSuccess,
      handleImageUploadError,
      handleImageRemove,
      handlePictureCardPreview,
      handleDelete,
      toggleProductStatus,
      disabledDate,
      handleSizeChange,
      handleCurrentChange,
      removeImage,
      getImageUrl,
      getImageUrlWithDomain,
      customImageUpload,
      customImagesUpload,
      memberLevels,
      loadingMemberLevels
    };
  }
};
</script>

<style scoped>
.appointment-products {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-area {
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.search-input {
  width: 500px;
}

.search-filters {
  display: flex;
  gap: 10px;
  align-items: center;
}

.search-filters .el-select {
  width: 150px;
}

.product-list-content {
  margin-top: 20px;
}

.product-table {
  margin-bottom: 20px;
}

/* 商品信息列样式 */
.product-info-column {
  display: flex;
  gap: 15px;
  align-items: center;
}

.product-image-container {
  flex-shrink: 0;
}

.product-details {
  flex: 1;
  min-width: 0;
}

.product-title {
  font-weight: 500;
  color: #333;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
}

.product-intro {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 时间标签样式 */
.time-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 2px;
}

.pagination-area {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* 弹窗样式 */
.product-form-content {
  max-height: 600px;
  overflow-y: auto;
}

.product-form {
  margin-top: 20px;
}

.el-upload {
  margin-bottom: 20px;
}

.rich-text-editor {
  min-height: 200px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

/* WangEditor 工具栏样式 */
.rich-text-editor :deep(.w-e-toolbar) {
  border-bottom: 1px solid #dcdfe6;
}

/* WangEditor 编辑区域样式 */
.rich-text-editor :deep(.w-e-text-container) {
  height: 300px;
  border: none;
}

/* 自定义标签页样式 - 悬浮效果 */
.custom-tabs {
  display: flex;
  border-bottom: 1px solid #e4e7ed;
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 100;
  padding: 10px 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 0; /* 移除底部边距，因为sticky定位会覆盖内容 */
}

.custom-tab {
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  background-color: #f5f7fa;
  border: 1px solid #e4e7ed;
  margin-right: 10px;
  border-radius: 4px 4px 0 0;
  transition: all 0.3s ease;
}

.custom-tab:hover {
  color: #409EFF;
  background-color: #ecf5ff;
}

.custom-tab.active {
  color: #fff;
  background-color: #409EFF;
  border-color: #409EFF;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 标签页内容样式 */
.product-form .el-tabs__content {
  margin-top: 20px;
  padding-top: 20px; /* 为悬浮tab留出空间 */
}
</style>